/**
 * From what somebody SAID (or typed for AI) to the to-dos the preview shows.
 *
 * Two roads in, one shape out:
 *
 *   AI allowed  - the server's POST /todos/assist/draft answers 1 to 10
 *                 drafts already matched to this person's own lists, labels
 *                 and people, with every day worked out on the server
 *                 (fromAiDraft just fills in the gaps);
 *   AI not, or  - the transcript goes through quick add's own parser
 *   AI failed     (lib/quickAdd.js), after spokenToLine has turned speech
 *                 into the words quick add reads: "priority one" is "p1",
 *                 "3 p.m." is "3pm", "hashtag Year end" is "#Year end", and
 *                 "remind me to" goes. draftsFromSpeech also learns the
 *                 commonest way one sentence is several to-dos - "Tuesday
 *                 and Wednesday 2 to 5" (23 October, Gordon's staff reviews)
 *                 - and splits it, one per day, with the time and duration.
 *
 * The draft: { title, notes, due_date, due_time, deadline, duration_minutes,
 * priority, label_ids, list_id, assignee_key, assignee_name, repeat,
 * reminder_minutes_before, heads_up, source: 'ai' | 'quick' }. Nothing is
 * made from it until the person presses Create.
 *
 * PURE - quick add's parser is passed in by import, the clock as `now` - so
 * e2e/todo-assist.mjs runs it in Node against a fixed morning.
 */
import { addDays, dateForTime, isoDay, parseQuickAdd } from './quickAdd';

const NUM = { one: 1, two: 2, three: 3, four: 4, '1': 1, '2': 2, '3': 3, '4': 4 };

/** A dictated sentence, as a line quick add can read. */
export function spokenToLine(text) {
  let s = String(text || '').replace(/\s+/g, ' ').trim();
  s = s.replace(/[.!?…]+$/u, '');
  s = s.replace(/^(?:ok(?:ay)?,?\s+)?(?:please,?\s+)?(?:remind me to|remember to|i need to|i have to|i've got to|i must|add a to-?do to|to-?do:?)\s+/i, '');
  // "I want to do the staff reviews": the work is "staff reviews".
  s = s.replace(/(^|\bweek,?\s+)(?:i want to|i'd like to|i'm going to|i will)(?:\s+do)?(?:\s+the)?\s+/i, '$1');
  // "priority one", "p one", "top priority", "high priority".
  s = s.replace(/\b(?:priority|p)\s+(one|two|three|four|[1-4])\b/gi, (m, n) => `p${NUM[n.toLowerCase()]}`);
  s = s.replace(/\btop priority\b/gi, 'p1').replace(/\bhigh priority\b/gi, 'p2');
  // "3:00 PM", "3 p.m.", "15:30" stays as it is.
  s = s.replace(/\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?\s?m\b\.?/gi,
    (m, h, mm, ap) => `${h}${mm && mm !== '00' ? `:${mm}` : ''}${ap.toLowerCase()}m`);
  // Speech has no '#': "hashtag Year end" / "hash tag Year end" is the list.
  s = s.replace(/\bhash\s?tag\s+/gi, '#');
  return s.trim();
}

/** What is left of a title once quick add has taken its words out: no stray commas, a capital. */
export function tidyTitle(t) {
  let s = String(t || '').replace(/\s+([,.;:!?])/g, '$1').replace(/([,;:])(?=[,;:.])/g, '')
    .replace(/^[\s,.;:–-]+|[\s,;:–-]+$/gu, '').replace(/\b(?:at|on|by|for|from)$/i, '').replace(/[\s,;:–-]+$/u, '').trim();
  if (s) s = s[0].toLocaleUpperCase() + s.slice(1);
  return s.slice(0, 300);
}

/* ----------------------------------------- the multi-day fallback */

const pad = (n) => String(n).padStart(2, '0');
const DAY_WORDS = { monday: 1, mon: 1, tuesday: 2, tues: 2, tue: 2, wednesday: 3, weds: 3, wed: 3, thursday: 4, thurs: 4,
  thur: 4, thu: 4, friday: 5, fri: 5, saturday: 6, sunday: 0 };
const DAY = '(monday|tuesday|wednesday|thursday|friday|saturday|sunday|mon|tues|tue|weds|wed|thurs|thur|thu|fri)';
const DAY_LIST = new RegExp(`(?:\\bon\\s+)?\\b${DAY}\\b((?:\\s*,\\s*|\\s+and\\s+|\\s*&\\s*)(?:on\\s+)?\\b${DAY}\\b)+`, 'i');
const DAY_SPAN = new RegExp(`(?:\\bfrom\\s+)?\\b${DAY}\\b\\s+(?:to|till|until|through|-)\\s+\\b${DAY}\\b`, 'i');
const RANGE = /(\bfrom\s+)?\b(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?\s*(?:to|till|until|-|–)\s*(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?(?![\w/])/i;

/**
 * "from 2pm to 5pm", "2 to 5", "2-5pm" as the words quick add reads: "at
 * 14:00 for 180 minutes". A bare "2-5" with no "from" and no am/pm is left
 * alone - it is a quantity as often as a time. Office hours decide a bare
 * hour: 1 to 7 is the afternoon.
 */
export function rangeToWords(line) {
  return String(line).replace(RANGE, (m, from, h1, m1, ap1, h2, m2, ap2) => {
    const conn = /\b(?:to|till|until)\b/i.test(m);
    if (!from && !ap1 && !ap2 && !conn) return m;
    const clock = (h, mm, ap, other) => {
      let n = Number(h);
      const a = ap || other;
      if (n > 23 || Number(mm || 0) > 59) return null;
      if (a === 'pm' && n < 12) n += 12;
      else if (a === 'am' && n === 12) n = 0;
      else if (!a && n >= 1 && n <= 7) n += 12;
      return n * 60 + Number(mm || 0);
    };
    const e = clock(h2, m2, ap2 && ap2.toLowerCase(), null);
    // "2 to 5pm": the start takes the end's half of the day when it fits.
    let s = clock(h1, m1, ap1 && ap1.toLowerCase(), !ap1 && ap2 && Number(h1) <= Number(h2) ? ap2.toLowerCase() : null);
    if (s === null || e === null) return m;
    if (e <= s && s - 720 >= 0 && !ap1) s -= 720;
    if (e <= s || e - s < 5) return m;
    return `at ${pad(Math.floor(s / 60))}:${pad(s % 60)} for ${e - s} minutes`;
  });
}

/** Monday of now's week (weeks Monday to Sunday), as a Date. */
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));

/**
 * The days a line names together, as ISO dates, or null: "Tuesday and
 * Wednesday", "Mon, Wed and Fri", "Tuesday to Thursday" (only with a time
 * for each day - one piece of work across days is one to-do). "Next week"
 * anywhere puts them in next Monday-to-Sunday week; otherwise each is the
 * next one after today, as quick add reads a weekday. "Every ..." is a
 * repeat and is never split.
 */
export function splitDays(line, now) {
  const s = String(line);
  if (/\bevery\b/i.test(s)) return null;
  let days = null;
  let hit = DAY_LIST.exec(s);
  if (hit) {
    days = [...hit[0].matchAll(new RegExp(`\\b${DAY}\\b`, 'gi'))].map((m) => DAY_WORDS[m[1].toLowerCase()]);
  } else if ((hit = DAY_SPAN.exec(s)) && /\bat \d{2}:\d{2} for \d+ minutes\b/.test(s)) {
    const a = DAY_WORDS[hit[1].toLowerCase()];
    const b = DAY_WORDS[hit[2].toLowerCase()];
    days = [];
    for (let d = a, i = 0; i < 7; d = (d + 1) % 7, i += 1) { days.push(d); if (d === b) break; }
  }
  if (!days || days.length < 2) return null;
  const nextWeek = /\bnext week\b/i.test(s);
  const base = mondayOf(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  const dates = [...new Set(days)].map((wd) => {
    if (nextWeek) return isoDay(addDays(base, 7 + ((wd + 6) % 7)));
    return isoDay(addDays(now, ((wd - now.getDay() + 7) % 7) || 7));
  });
  const rest = (s.slice(0, hit.index) + ' {{DAY}} ' + s.slice(hit.index + hit[0].length))
    .replace(/,?\s*\bnext week\b,?/i, ' ').replace(/\s+/g, ' ').trim();
  return dates.map((d) => rest.replace('{{DAY}}', d));
}

/** One line read by quick add's parser, as a draft. */
function draftFromLine(line, text, { now, lists = [], labels = [], people = [], defaultListId = null } = {}) {
  const p = parseQuickAdd(line, { now, lists, labels, people, defaultListId });
  let due_date = p.due_date || null;
  if (!due_date && p.due_time) due_date = dateForTime(p.due_time, now);
  return {
    title: tidyTitle(p.title) || tidyTitle(line) || String(text || '').trim().slice(0, 300),
    notes: null,
    due_date,
    due_time: p.due_time || null,
    deadline: p.deadline || null,
    duration_minutes: p.duration_minutes || null,
    priority: p.priority || 4,
    label_ids: p.labels.filter((b) => b.id).map((b) => b.id),
    list_id: p.list ? p.list.id : null,
    assignee_key: p.assignee ? p.assignee.key : null,
    assignee_name: p.assignee ? p.assignee.name : null,
    repeat: p.repeat || null,
    reminder_minutes_before: null,
    heads_up: [],
    source: 'quick'
  };
}

/**
 * The transcript read by quick add's parser, as one draft per day it names
 * together ("Tuesday and Wednesday 2 to 5" is two), or one. Only labels that
 * already exist are kept (a spoken "@new" is not a label anybody said yes
 * to); "+Name" is a member of the list it goes into, or somebody to send to.
 */
export function draftsFromSpeech(text, opts = {}) {
  const line = rangeToWords(spokenToLine(text));
  const lines = splitDays(line, opts.now || new Date()) || [line];
  return lines.map((l) => draftFromLine(l, text, opts));
}

/** The first of draftsFromSpeech - for a caller that wants one. */
export const draftFromSpeech = (text, opts = {}) => draftsFromSpeech(text, opts)[0];

/** The server's AI draft, checked once more on this side and given the same shape. */
export function fromAiDraft(d) {
  const day = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
  return {
    title: String(d?.title || '').trim().slice(0, 300),
    notes: typeof d?.notes === 'string' && d.notes.trim() ? d.notes.trim() : null,
    due_date: day(d?.due_date),
    due_time: typeof d?.due_time === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(d.due_time) ? d.due_time : null,
    deadline: day(d?.deadline),
    duration_minutes: Number.isInteger(d?.duration_minutes) ? d.duration_minutes : null,
    priority: [1, 2, 3, 4].includes(d?.priority) ? d.priority : 4,
    label_ids: Array.isArray(d?.label_ids) ? d.label_ids.filter((x) => typeof x === 'string') : [],
    list_id: typeof d?.list_id === 'string' ? d.list_id : null,
    assignee_key: typeof d?.assignee_key === 'string' ? d.assignee_key : null,
    assignee_name: typeof d?.assignee_name === 'string' ? d.assignee_name : null,
    repeat: d?.repeat && typeof d.repeat === 'object' && typeof d.repeat.freq === 'string' ? d.repeat : null,
    reminder_minutes_before: Number.isInteger(d?.reminder_minutes_before) && d.reminder_minutes_before >= 0 ? d.reminder_minutes_before : null,
    heads_up: Array.isArray(d?.heads_up) ? d.heads_up.filter((x) => typeof x === 'string').slice(0, 3) : [],
    source: 'ai'
  };
}

/** The server's answer as drafts: `drafts` when there are, else the one `draft` (an older server). */
export function fromAiAnswer(data) {
  const rows = Array.isArray(data?.drafts) && data.drafts.length ? data.drafts : data?.draft ? [data.draft] : [];
  return rows.slice(0, 10).map(fromAiDraft).filter((d) => d.title);
}

/* ------------------------------------------------------- the language chip */

const LANG_KEY = 'jarvis_voice_language';
const SINGLE_KEY = 'jarvis_voice_language_single';
export const AUTO = 'auto';

const read = (k) => { try { return localStorage.getItem(k); } catch { return null; } };

/**
 * The language to dictate in: the one last chosen, if it is one of
 * `languages` and usable now; else Auto (Malti + English) when AI help is on
 * - the default - or the last single language, or English. Auto needs AI:
 * with it off, the person's own single language is used instead.
 */
export function rememberedLanguage(languages, { ai = false } = {}) {
  const codes = (languages || []).map((l) => l.code);
  const code = read(LANG_KEY);
  const single = read(SINGLE_KEY);
  const fallback = codes.includes(single) && single !== AUTO ? single : (codes.includes('en-GB') ? 'en-GB' : codes.find((c) => c !== AUTO) || 'en-GB');
  if (code === AUTO || !code) return ai && codes.includes(AUTO) ? AUTO : fallback;
  return codes.includes(code) ? code : fallback;
}

/** The single language Auto falls back to (for the server's `fallback`). */
export function singleLanguage(languages) {
  const codes = (languages || []).map((l) => l.code);
  const single = read(SINGLE_KEY);
  const code = read(LANG_KEY);
  if (codes.includes(code) && code !== AUTO) return code;
  return codes.includes(single) && single !== AUTO ? single : 'en-GB';
}

export function rememberLanguage(code) {
  try {
    localStorage.setItem(LANG_KEY, code);
    if (code !== AUTO) localStorage.setItem(SINGLE_KEY, code);
  } catch { /* kept for this visit only */ }
}
